/**
 * W93 (#139) — Kapitulations-Radar fürs Dashboard.
 *
 * Liest die tägliche Markt-Interna-Zeile (18:15-Cron → v5_market_internals):
 * Kapitulations-Score (Term-Structure + VIX + Breite-Wäsche + neue Tiefs)
 * und den Zweig-Breadth-Thrust als Boden-BESTÄTIGUNGS-Signal.
 *
 * Lesart für den Antizykliker: hoher Score = Panik = Kaufzonen entstehen
 * (Limits vorbereiten); Thrust 🟢 = Markt hat den Boden bestätigt.
 */

import { Siren } from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
import { supabase } from '@/lib/supabase';
import { CARD, CARD_HEADER } from '@/lib/design-tokens';
import { CardInfoIcon } from '@/components/ui/CardInfoIcon';
import { cn } from '@/lib/utils';

export interface InternalsRow {
  as_of_date: string;
  pct_above_ma50: number | null;
  pct_above_ma200: number | null;
  new_lows_20d_pct: number | null;
  vix: number | null;
  vix9d: number | null;
  vix3m: number | null;
  term_structure: 'contango' | 'partial_inversion' | 'backwardation' | null;
  thrust_state: 'none' | 'setup' | 'thrust_fired' | null;
  thrust_fired_date: string | null;
  capitulation_score: number | null;
  capitulation_flag: 'none' | 'elevated' | 'capitulation' | null;
  reasons: Array<{ label: string; value_text: string; points: number; max: number }> | null;
  summary: string | null;
}

export async function fetchLatestInternals(signal?: AbortSignal): Promise<InternalsRow | null> {
  let q = supabase
    .from('v5_market_internals')
    .select('*')
    .order('as_of_date', { ascending: false })
    .limit(1);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw new Error(`v5_market_internals: ${error.message}`);
  return (data?.[0] as InternalsRow | undefined) ?? null;
}

const FLAG_META = {
  capitulation: {
    cls: 'border-red-600/60 bg-red-950/40 text-red-200',
    label: 'KAPITULATION',
  },
  elevated: {
    cls: 'border-amber-600/50 bg-amber-950/30 text-amber-200',
    label: 'Erhöhter Stress',
  },
  none: {
    cls: 'border-zinc-700 bg-zinc-900/40 text-zinc-300',
    label: 'Normal',
  },
} as const;

export function CapitulationRadarCard() {
  const state = useDataState({
    queryKey: ['market-internals-latest'],
    queryFn: (signal) => fetchLatestInternals(signal),
    ttlSeconds: 30 * 60,
  });
  const row = state.state.data;
  if (!row || row.capitulation_score === null) return null;

  const flag = row.capitulation_flag ?? 'none';
  const meta = FLAG_META[flag];
  const thrustFired = row.thrust_state === 'thrust_fired';

  return (
    <section className={cn(CARD, 'space-y-2')}>
      <header className="flex items-center justify-between">
        <h3 className={CARD_HEADER}>
          <Siren className="size-4" /> Kapitulations-Radar
          <CardInfoIcon hint="Breite über die eigene Watchlist (kein NYSE-Universum), VIX9D/3M von CBOE. Hoher Score = Panik-Klimax-Nähe (antizyklische Kaufzonen entstehen) — kein Verkaufssignal. Zweig-Thrust (10T-EMA Advancer von <0,40 auf >0,615 in ≤10 Tagen) bestätigt historisch Böden." />
        </h3>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
          {row.as_of_date}
        </span>
      </header>

      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded border px-2 py-0.5 text-sm font-mono font-semibold',
            meta.cls
          )}
        >
          {row.capitulation_score}/100 · {meta.label}
        </span>
        {thrustFired && (
          <span className="inline-flex items-center rounded border border-emerald-600/60 bg-emerald-950/40 px-2 py-0.5 text-[11px] text-emerald-200">
            🟢 Breadth-Thrust {row.thrust_fired_date ? `(${row.thrust_fired_date})` : ''} — Boden bestätigt
          </span>
        )}
        {row.thrust_state === 'setup' && (
          <span className="inline-flex items-center rounded border border-zinc-700 bg-zinc-900/40 px-2 py-0.5 text-[11px] text-zinc-400">
            Thrust-Setup aktiv (Breite ausgewaschen)
          </span>
        )}
      </div>

      {row.reasons && row.reasons.length > 0 && (
        <ul className="space-y-0.5">
          {row.reasons.map((r) => (
            <li key={r.label} className="flex justify-between gap-2 text-[11px]">
              <span className="text-zinc-400">{r.label}</span>
              <span className="text-zinc-300 font-mono">
                {r.value_text} · {r.points}/{r.max} P
              </span>
            </li>
          ))}
        </ul>
      )}

      {row.summary && (
        <div className="text-[11px] text-zinc-300 leading-snug border-t border-zinc-800 pt-1.5">
          {row.summary}
        </div>
      )}

    </section>
  );
}
